/**
 * @fileoverview Terms of Service page
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { FileText, Shield, Users, AlertTriangle, CheckCircle, XCircle, Globe } from 'lucide-react';
import { HelpPageLayout } from '../../components/help/HelpPageLayout';

const TermsOfService: React.FC = () => {
  const { t } = useTranslation();

  const lastUpdated = "August 13, 2024";

  const sections = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Acceptance of Terms",
      content: [
        "By accessing and using Spinner Games !!, you accept and agree to be bound by the terms and provision of this agreement.",
        "If you do not agree to abide by the above, please do not use this service.",
        "We reserve the right to modify these terms at any time, and such modifications shall be effective immediately upon posting."
      ]
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "User Accounts",
      content: [
        "You may create an account to access additional features and save your preferences.",
        "You are responsible for maintaining the confidentiality of your account information.",
        "You must provide accurate and complete information when creating your account.",
        "You are responsible for all activities that occur under your account."
      ]
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Acceptable Use",
      content: [
        "Use the service for lawful purposes only.",
        "Do not upload, post, or transmit any content that is harmful, offensive, or inappropriate.",
        "Do not attempt to gain unauthorized access to our systems or other users' accounts.",
        "Do not use the service to spam, harass, or harm others.",
        "Respect the intellectual property rights of others."
      ]
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "User-Generated Content",
      content: [
        "You retain ownership of content you create and share.",
        "By sharing content, you grant us a license to use it for service improvement.",
        "You are responsible for the content you create and share.",
        "We reserve the right to remove inappropriate content.",
        "Content must comply with our community guidelines."
      ]
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: "Prohibited Activities",
      content: [
        "Creating or sharing harmful, offensive, or inappropriate content",
        "Attempting to hack, disrupt, or interfere with our services",
        "Using automated tools to access our services without permission",
        "Impersonating others or creating fake accounts",
        "Collecting personal information from other users without consent",
        "Using the service for commercial purposes without permission"
      ]
    },
    {
      icon: <XCircle className="w-6 h-6" />,
      title: "Termination",
      content: [
        "We may terminate or suspend your account at any time for violations of these terms.",
        "You may terminate your account at any time by contacting us.",
        "Upon termination, your right to use the service will cease immediately.",
        "We may retain certain information as required by law or for legitimate business purposes."
      ]
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Artificial Intelligence and Translation Services",
      content: [
        "Our service utilizes artificial intelligence (AI) technology for translation services to provide content in multiple languages.",
        "AI-generated translations may contain errors, inaccuracies, or may not fully capture the intended meaning of the original content.",
        "Users should be aware that translations are performed by machines and may not be perfect.",
        "We are not responsible for any misunderstandings or issues that may arise from AI-generated translations.",
        "For critical information, we recommend referring to the original language version or consulting with a qualified human translator.",
        "By using our translation services, you acknowledge and accept the limitations of AI-generated content."
      ]
    }
  ];

  const disclaimers = [
    {
      title: "Service Availability",
      content: "We strive to provide reliable service but cannot guarantee uninterrupted access. We may temporarily suspend the service for maintenance or updates."
    },
    {
      title: "Content Accuracy",
      content: "While we strive for accuracy, we cannot guarantee that all content is error-free or up-to-date. Users should verify important information independently."
    },
    {
      title: "Third-Party Services",
      content: "Our service may integrate with third-party services. We are not responsible for the content or practices of these external services."
    },
    {
      title: "Advertising and Monetization",
      content: "To manage our operations and provide this app free to users, we may display Google Ads and other advertising content. We may also use affiliate links in our blog content. These advertisements and affiliate links help us maintain and improve our service while keeping it accessible to all users at no cost."
    },
    {
      title: "Limitation of Liability",
      content: "To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages."
    },
    {
      title: "Artificial Intelligence and Translation Services",
      content: "Our service utilizes artificial intelligence (AI) technology for translation services to provide content in multiple languages. While we strive for accuracy, AI-generated translations may contain errors, inaccuracies, or may not fully capture the intended meaning of the original content. Users should be aware that translations are performed by machines and may not be perfect. We are not responsible for any misunderstandings or issues that may arise from AI-generated translations. For critical information, we recommend referring to the original language version or consulting with a qualified human translator."
    }
  ];

  const intellectualProperty = [
    "All content, features, and functionality of our service are owned by us or our licensors.",
    "Our trademarks, service marks, and logos are protected by intellectual property laws.",
    "You may not use our intellectual property without our express written consent.",
    "User-generated content remains the property of the user who created it."
  ];

  return (
    <HelpPageLayout>
      <Helmet>
        <title>Terms of Service - Spinner Games !!</title>
        <meta name="description" content="Read our terms of service to understand the rules and guidelines for using our spinner app." />
                  <meta property="og:title" content="Terms of Service - Spinner Games !!" />
        <meta property="og:description" content="Terms and conditions for using our interactive spinner application." />
        <link rel="canonical" href="https://spinnergames.fun/help/terms" />
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Terms of Service",
          "description": "Terms of service for Spinner Games !!",
          "dateModified": lastUpdated,
          "publisher": {
            "@type": "Organization",
            "name": "Spinner Games !!"
          }
        })}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Terms of Service
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-4">
            Please read these terms carefully before using our service
          </p>
          <p className="text-gray-500 dark:text-gray-400">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Introduction */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8">
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">
            These Terms of Service ("Terms") govern your use of the Spinner Games !! application and related services 
            (collectively, the "Service"). By accessing or using our Service, you agree to be bound by these Terms.
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300">
            If you disagree with any part of these terms, then you may not access the Service. These Terms apply to all 
            visitors, users, and others who access or use the Service.
          </p>
        </div>

        {/* Main sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300 mr-4">
                  {section.icon}
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {section.title}
                </h2>
              </div>
              
              <div className="space-y-4">
                {section.content.map((item, itemIndex) => (
                  <p key={itemIndex} className="text-gray-600 dark:text-gray-300">
                    {item}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Intellectual Property */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Intellectual Property Rights
          </h2>
          <div className="space-y-4">
            {intellectualProperty.map((item, index) => (
              <p key={index} className="text-gray-600 dark:text-gray-300">
                {item}
              </p>
            ))}
          </div>
        </div>

        {/* Disclaimers */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Disclaimers and Limitations
          </h2>
          <div className="space-y-6">
            {disclaimers.map((disclaimer, index) => (
              <div key={index}>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {disclaimer.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {disclaimer.content}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Advertising and Affiliate Disclosure */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Advertising and Affiliate Disclosure
          </h2>
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              <strong>Google Ads and Advertising:</strong> To manage our operations and provide this application free to users, 
              we may display Google Ads and other advertising content throughout our service. These advertisements 
              help us cover operational costs and maintain the quality of our free service.
            </p>
            <p className="text-gray-600 dark:text-gray-300">
              <strong>Affiliate Links:</strong> Our blog content may contain affiliate links to third-party products or services. 
              When you click on these links and make a purchase, we may receive a commission at no additional cost to you. 
              These affiliate partnerships help us generate revenue to support our free service.
            </p>
            <p className="text-gray-600 dark:text-gray-300">
              <strong>Transparency:</strong> We are committed to transparency in our monetization practices. All affiliate links 
              will be clearly disclosed, and we will only promote products or services that we believe provide value to our users.
            </p>
            <p className="text-gray-600 dark:text-gray-300">
              <strong>User Choice:</strong> You are not required to click on any advertisements or affiliate links. 
              Our core service remains free and accessible regardless of whether you interact with these monetization elements.
            </p>
          </div>
        </div>

        {/* Privacy and Data */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Privacy and Data Protection
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Your privacy is important to us. Our collection and use of personal information is governed by our 
            Privacy Policy, which is incorporated into these Terms by reference.
          </p>
          <p className="text-gray-600 dark:text-gray-300">
            By using our Service, you consent to the collection and use of information in accordance with our 
            Privacy Policy.
          </p>
        </div>

        {/* Governing Law */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Governing Law and Disputes
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], 
            without regard to its conflict of law provisions.
          </p>
          <p className="text-gray-600 dark:text-gray-300">
            Any disputes arising from these Terms or your use of the Service shall be resolved through 
            binding arbitration or in the courts of [Your Jurisdiction].
          </p>
        </div>

        {/* Changes to Terms */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Changes to Terms
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            We reserve the right to modify or replace these Terms at any time. If a revision is material, 
            we will try to provide at least 30 days notice prior to any new terms taking effect.
          </p>
          <p className="text-gray-600 dark:text-gray-300">
            What constitutes a material change will be determined at our sole discretion. By continuing to 
            access or use our Service after any revisions become effective, you agree to be bound by the 
            revised terms.
          </p>
        </div>

        {/* Contact Information */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Contact Information
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div className="space-y-2 text-gray-600 dark:text-gray-300">
            <p>Email: legal@spinnergames.fun</p>
            <p>Contact Form: <a href="/help/contact" className="text-blue-600 dark:text-blue-400 hover:underline">Contact Us</a></p>
          </div>
        </div>

        {/* Back to Help */}
        <div className="text-center mt-8">
          <a
            href="/help"
            className="inline-flex items-center px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors"
          >
            Back to Help Center
          </a>
        </div>
      </div>
    </HelpPageLayout>
  );
};

export default TermsOfService; 